import { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import * as htmlToImage from 'html-to-image';

import { Hero } from './components/Hero';
import { Topbar } from './components/Topbar';
import { ToolPanel } from './components/ToolPanel';
import { LayersPanel } from './components/LayersPanel';
import { CanvasArea } from './components/CanvasArea';
import { ExportModal } from './components/ExportModal';
import { GiftLinkModal } from './components/GiftLinkModal';
import { GiftView } from './components/GiftView';
import { MobileBottomBar } from './components/MobileBottomBar';
import {
  CANVAS_SIZE,
  uid,
  readFileAsDataURL,
  loadImage,
  computeLayerSize,
} from './lib/canvas-utils';
import { smartAutoArrange } from './lib/auto-arrange';
import { getContentBounds, cropDataUrl } from './lib/auto-crop';
import { exportFreePreview } from './lib/watermark';
import { makePreviewFromDataUrl } from './lib/preview-from-dataurl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_HISTORY = 50;
const HD_EXPORT_SIZE = 5000; // px — paid HD download long-edge resolution

function useIsMobile(breakpoint = '(max-width: 1023px)') {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(breakpoint).matches : false
  );
  useEffect(() => {
    const m = window.matchMedia(breakpoint);
    const h = (e) => setMatches(e.matches);
    m.addEventListener('change', h);
    return () => m.removeEventListener('change', h);
  }, [breakpoint]);
  return matches;
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function Editor({ onBackHome }) {
  const isMobile = useIsMobile();
  const [layers, setLayers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [background, setBackground] = useState({ type: 'solid', color: '#FFFFFF' });
  const [arrangingMode, setArrangingMode] = useState(null);
  const [lastStyleName, setLastStyleName] = useState(null);

  // Export modal + payment flow
  const [exportOpen, setExportOpen] = useState(false);
  const [busyMode, setBusyMode] = useState(null); // 'free' | 'paid' | null
  const [paymentStatus, setPaymentStatus] = useState(null); // 'awaiting' | 'paid' | 'cancelled' | null
  const pendingExportRef = useRef(null); // { format, crop, sessionId }
  const pollRef = useRef(null);
  const popupRef = useRef(null);

  // Gift link state
  const [giftLink, setGiftLink] = useState(null); // { giftId, expiresAt }
  const [giftModalOpen, setGiftModalOpen] = useState(false);

  const canvasRef = useRef(null);

  // ---------- History (undo/redo) ----------
  const historyRef = useRef([]);
  const redoRef = useRef([]);
  const [historyVer, setHistoryVer] = useState(0);

  const commit = useCallback(() => {
    historyRef.current.push({
      layers: JSON.parse(JSON.stringify(layers)),
      background: JSON.parse(JSON.stringify(background)),
    });
    if (historyRef.current.length > MAX_HISTORY) historyRef.current.shift();
    redoRef.current = [];
    setHistoryVer((v) => v + 1);
  }, [layers, background]);

  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return;
    redoRef.current.push({
      layers: JSON.parse(JSON.stringify(layers)),
      background: JSON.parse(JSON.stringify(background)),
    });
    if (redoRef.current.length > MAX_HISTORY) redoRef.current.shift();
    const prev = historyRef.current.pop();
    setLayers(prev.layers);
    setBackground(prev.background);
    setSelectedId(null);
    setHistoryVer((v) => v + 1);
  }, [layers, background]);

  const redo = useCallback(() => {
    if (redoRef.current.length === 0) return;
    historyRef.current.push({
      layers: JSON.parse(JSON.stringify(layers)),
      background: JSON.parse(JSON.stringify(background)),
    });
    if (historyRef.current.length > MAX_HISTORY) historyRef.current.shift();
    const next = redoRef.current.pop();
    setLayers(next.layers);
    setBackground(next.background);
    setSelectedId(null);
    setHistoryVer((v) => v + 1);
  }, [layers, background]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      const k = (e.key || '').toLowerCase();
      // Avoid hijacking shortcuts when typing in inputs/textareas
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (k === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((k === 'z' && e.shiftKey) || k === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  /* ---------- layer ops (each calls commit() before mutating) ---------- */
  const addImageLayerInternal = useCallback(async (file, extra = {}) => {
    const src = await readFileAsDataURL(file);
    const img = await loadImage(src);
    const size = computeLayerSize(img.naturalWidth, img.naturalHeight);
    const id = uid();
    setLayers((prev) => [
      ...prev,
      {
        id,
        src,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
        ...size,
        ...extra,
        zIndex: prev.length,
      },
    ]);
    setSelectedId(id);
  }, []);

  const onUploadFiles = useCallback(async (files) => {
    commit();
    let added = 0;
    for (const f of files) {
      try {
        await addImageLayerInternal(f);
        added += 1;
      } catch (e) {
        console.error(e);
      }
    }
    if (added) toast.success(`${added} image${added > 1 ? 's' : ''} added`);
    else toast.error("Couldn't read those images.");
  }, [commit, addImageLayerInternal]);

  const onAddLibraryImage = useCallback(async (url, name, tint) => {
    try {
      commit();
      // Fetch the library asset as a blob so we can convert to data URL —
      // this keeps the exported canvas fully self-contained (same as uploads).
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], `${name || 'library'}.png`, { type: blob.type || 'image/png' });
      await addImageLayerInternal(file, tint ? { tint } : {});
      toast.success(`${name || 'Image'} added`);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load that image.");
    }
  }, [commit, addImageLayerInternal]);

  const updateLayer = useCallback((id, patch) => {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }, []);

  const setLayerTint = useCallback((id, tint) => {
    commit();
    updateLayer(id, { tint });
  }, [commit, updateLayer]);

  const deleteLayer = useCallback((id) => {
    commit();
    setLayers((prev) => {
      const filtered = prev.filter((l) => l.id !== id);
      const sorted = filtered.sort((a, b) => a.zIndex - b.zIndex).map((l, i) => ({ ...l, zIndex: i }));
      return sorted;
    });
    setSelectedId((s) => (s === id ? null : s));
    toast('Layer removed');
  }, [commit]);

  const duplicateLayer = useCallback((id) => {
    commit();
    const newId = uid();
    setLayers((prev) => {
      const src = prev.find((l) => l.id === id);
      if (!src) return prev;
      const newLayer = {
        ...src,
        id: newId,
        x: src.x + 30,
        y: src.y + 30,
        zIndex: prev.length,
      };
      return [...prev, newLayer];
    });
    setSelectedId(newId);
  }, [commit]);

  const moveLayerZ = useCallback((id, dir) => {
    commit();
    setLayers((prev) => {
      const sorted = [...prev].sort((a, b) => a.zIndex - b.zIndex);
      const idx = sorted.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const swapWith = dir === 'up' ? idx + 1 : idx - 1;
      if (swapWith < 0 || swapWith >= sorted.length) return prev;
      const a = sorted[idx];
      const b = sorted[swapWith];
      return prev.map((l) => {
        if (l.id === a.id) return { ...l, zIndex: b.zIndex };
        if (l.id === b.id) return { ...l, zIndex: a.zIndex };
        return l;
      });
    });
  }, [commit]);

  const resetTransform = useCallback((id) => {
    commit();
    setLayers((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const max = CANVAS_SIZE * 0.6;
        const ratio =
          l.naturalWidth && l.naturalHeight
            ? l.naturalWidth / l.naturalHeight
            : l.width / Math.max(1, l.height);
        let w = l.naturalWidth || l.width;
        let h = l.naturalHeight || l.height;
        if (w > max || h > max) {
          if (w > h) { w = max; h = max / ratio; }
          else { h = max; w = max * ratio; }
        }
        return {
          ...l,
          width: w,
          height: h,
          rotation: 0,
          x: (CANVAS_SIZE - w) / 2,
          y: (CANVAS_SIZE - h) / 2,
        };
      })
    );
  }, [commit]);

  const clearAll = useCallback(() => {
    if (layers.length === 0) return;
    commit();
    setLayers([]);
    setSelectedId(null);
    setLastStyleName(null);
    toast('Canvas cleared');
  }, [layers.length, commit]);

  const setBackgroundWithHistory = useCallback((next) => {
    commit();
    setBackground(typeof next === 'function' ? next(background) : next);
  }, [commit, background]);

  /* ---------- auto-arrange ---------- */
  const applyLayouts = useCallback((layouts) => {
    setLayers((prev) => {
      const byId = Object.fromEntries(layouts.map((l) => [l.id, l]));
      const orderedIds = layouts.map((l) => l.id);
      return prev.map((l) => {
        const lay = byId[l.id];
        if (!lay) return l;
        const z = orderedIds.indexOf(l.id);
        return {
          ...l,
          x: lay.x, y: lay.y,
          width: lay.width, height: lay.height,
          rotation: lay.rotation,
          zIndex: z >= 0 ? z : l.zIndex,
        };
      });
    });
  }, []);

  const onSmartArrange = useCallback(() => {
    if (!layers.length) return;
    commit();
    setArrangingMode('smart');
    setTimeout(() => {
      const { style, layouts } = smartAutoArrange(layers, CANVAS_SIZE, lastStyleName);
      applyLayouts(layouts);
      setLastStyleName(style);
      setSelectedId(null);
      setArrangingMode(null);
      toast.success(`Arranged · ${style}`);
    }, 60);
  }, [layers, lastStyleName, applyLayouts, commit]);

  const onAiArrange = useCallback(async (hint) => {
    if (!layers.length) return;
    commit();
    setArrangingMode('ai');
    try {
      const payload = {
        canvas_size: CANVAS_SIZE,
        layers: layers.map((l) => ({
          id: l.id,
          aspect_ratio: l.naturalWidth / Math.max(1, l.naturalHeight),
        })),
        style_hint: hint || undefined,
      };
      const res = await axios.post(`${API}/ai/auto-arrange`, payload, { timeout: 45000 });
      const { layouts, style } = res.data || {};
      if (!layouts?.length) throw new Error('No layouts returned');
      applyLayouts(layouts);
      setLastStyleName(`AI · ${style || 'creative'}`);
      setSelectedId(null);
      toast.success(`AI arranged · ${style || 'creative'}`);
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.detail || e.message || 'AI arrange failed';
      toast.error(`AI arrange failed: ${msg}`);
    } finally {
      setArrangingMode(null);
    }
  }, [layers, applyLayouts, commit]);

  /* ---------- HD export (after payment) ---------- */
  const renderHd = useCallback(async (format, crop) => {
    if (!canvasRef.current) return null;
    const node = canvasRef.current;
    const rect = node.getBoundingClientRect();
    const pixelRatio = HD_EXPORT_SIZE / Math.max(1, rect.width);
    const baseOpts = {
      pixelRatio,
      cacheBust: true,
      width: rect.width,
      height: rect.height,
      skipFonts: true,
    };
    let dataUrl;
    if (format === 'png') {
      dataUrl = await htmlToImage.toPng(node, baseOpts);
    } else {
      dataUrl = await htmlToImage.toJpeg(node, {
        ...baseOpts,
        quality: 0.95,
        backgroundColor: background.type === 'transparent' ? '#ffffff' : undefined,
      });
    }
    if (crop && layers.length > 0) {
      const bounds = getContentBounds(layers, 16);
      if (bounds && bounds.width > 4 && bounds.height > 4) {
        const mime = format === 'png' ? 'image/png' : 'image/jpeg';
        dataUrl = await cropDataUrl(dataUrl, bounds, mime, 0.95);
      }
    }
    return dataUrl;
  }, [layers, background.type]);

  /* ---------- free preview ---------- */
  const handleFreePreview = useCallback(async (format) => {
    if (!canvasRef.current) return;
    setBusyMode('free');
    try {
      const dataUrl = await exportFreePreview(canvasRef.current, {
        format,
        previewSize: 512,
        bgFill: background.type === 'transparent' ? '#ffffff' : undefined,
      });
      const suffix = format === 'png' ? 'png' : 'jpg';
      downloadDataUrl(dataUrl, `stckrstckr-preview-${Date.now()}.${suffix}`);
      toast.success('Preview downloaded · 512px watermarked');
      setExportOpen(false);
    } catch (e) {
      console.error(e);
      toast.error('Preview failed.');
    } finally {
      setBusyMode(null);
    }
  }, [background.type]);

  /* ---------- paid checkout flow ---------- */
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // After-payment download trigger (shared by polling + postMessage paths)
  const finishPaidExport = useCallback(async (sessionId) => {
    const pending = pendingExportRef.current;
    if (!pending || pending.sessionId !== sessionId) return;
    pendingExportRef.current = null; // prevent double-download
    stopPolling();
    try {
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    } catch (_) { /* ignore */ }
    setPaymentStatus('paid');
    toast.success('Payment received · preparing HD download');
    const dataUrl = await renderHd(pending.format, pending.crop);
    if (dataUrl) {
      const suffix = pending.format === 'png' ? 'png' : 'jpg';
      const cropTag = pending.crop ? '-cropped' : '';
      downloadDataUrl(dataUrl, `stckrstckr-hd-${Date.now()}${cropTag}.${suffix}`);
      toast.success('HD file downloaded');

      // ---- "Save the world" gift-link mint ----
      if (pending.shareLink !== false) {
        try {
          const previewDataUrl = await makePreviewFromDataUrl(dataUrl, 480);
          const giftRes = await axios.post(`${API}/gift/create`, {
            hd_data_url: dataUrl,
            preview_data_url: previewDataUrl,
            format: pending.format,
            cropped: !!pending.crop,
            payment_session_id: sessionId,
          }, { timeout: 60000 });
          if (giftRes.data?.gift_id) {
            setGiftLink({
              giftId: giftRes.data.gift_id,
              expiresAt: giftRes.data.expires_at,
            });
            // Briefly delay so the HD download toast is visible first
            setTimeout(() => setGiftModalOpen(true), 500);
          }
        } catch (giftErr) {
          console.warn('Gift link mint failed', giftErr);
          toast.error("Couldn't create share link — but your HD file is downloaded.");
        }
      } else {
        // User opted out — just confirm the private download
        toast('Private download · no share link generated');
      }
    }
    setExportOpen(false);
    setBusyMode(null);
    setPaymentStatus(null);
  }, [renderHd, stopPolling]);

  // Listen for postMessage from Stripe success popup
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (!e.data || e.data.type !== 'stickr-payment-complete') return;
      finishPaidExport(e.data.sessionId);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [finishPaidExport]);

  // Cleanup on unmount
  useEffect(() => () => {
    stopPolling();
    if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
  }, [stopPolling]);

  const handleBuyHd = useCallback(async (format, crop, shareLink = true) => {
    // Open popup synchronously to avoid blockers
    const popup = window.open('about:blank', 'stickr_checkout', 'width=520,height=720');
    if (!popup) {
      toast.error('Allow popups for StckrStckr to complete payment.');
      return;
    }
    popupRef.current = popup;
    setBusyMode('paid');
    setPaymentStatus(null);
    pendingExportRef.current = { format, crop, shareLink, sessionId: null };

    try {
      const res = await axios.post(`${API}/checkout/create-session`, {
        package_id: 'export_hd',
        origin_url: window.location.origin,
        metadata: { format, cropped: String(crop) },
      });
      const { url, session_id } = res.data || {};
      if (!url) throw new Error('No checkout url');
      pendingExportRef.current.sessionId = session_id;

      // Redirect popup to Stripe
      try { popup.location.href = url; } catch (_) { /* ignore */ }
      setPaymentStatus('awaiting');

      // Poll status
      let attempts = 0;
      stopPolling();
      pollRef.current = setInterval(async () => {
        attempts += 1;
        try {
          const s = await axios.get(`${API}/checkout/status/${session_id}`);
          const data = s.data || {};
          if (data.payment_status === 'paid') {
            await finishPaidExport(session_id);
            return;
          }
          if (data.status === 'expired') {
            stopPolling();
            setPaymentStatus('cancelled');
            setBusyMode(null);
            toast.error('Checkout expired.');
            return;
          }
        } catch (_) {
          /* keep polling on transient errors */
        }
        // Detect popup closed without success
        if (popupRef.current && popupRef.current.closed && pendingExportRef.current?.sessionId === session_id) {
          if (attempts > 2) {
            stopPolling();
            setPaymentStatus('cancelled');
            setBusyMode(null);
          }
        }
        if (attempts > 150) { // ~5 min
          stopPolling();
          setPaymentStatus('cancelled');
          setBusyMode(null);
          toast.error('Payment timed out.');
        }
      }, 2000);
    } catch (e) {
      console.error(e);
      toast.error('Could not start checkout');
      try { popup.close(); } catch (_) { /* ignore */ }
      setBusyMode(null);
      setPaymentStatus(null);
    }
  }, [stopPolling, finishPaidExport]);

  /* ---------- render ---------- */
  const canUndo = historyRef.current.length > 0;
  const canRedo = redoRef.current.length > 0;
  // historyVer is read so React refreshes button enable state
  void historyVer;

  return (
    <div className="App flex flex-col h-screen bg-canvas">
      <Topbar
        onBackHome={onBackHome}
        onOpenExport={() => { setSelectedId(null); setExportOpen(true); }}
        onClear={clearAll}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
      />
      <main className="flex-1 flex overflow-hidden">
        {!isMobile && (
          <ToolPanel
            onUploadFiles={onUploadFiles}
            onAddLibraryImage={onAddLibraryImage}
            background={background}
            setBackground={setBackgroundWithHistory}
            onSmartArrange={onSmartArrange}
            onAiArrange={onAiArrange}
            layerCount={layers.length}
            arrangingMode={arrangingMode}
            lastStyleName={lastStyleName}
          />
        )}
        <CanvasArea
          ref={canvasRef}
          layers={layers}
          background={background}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onChange={updateLayer}
          onDeselect={() => setSelectedId(null)}
          onInteractionStart={commit}
          onSetTint={setLayerTint}
        />
        {!isMobile && (
          <LayersPanel
            layers={layers}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onMoveUp={(id) => moveLayerZ(id, 'up')}
            onMoveDown={(id) => moveLayerZ(id, 'down')}
            onDelete={deleteLayer}
            onDuplicate={duplicateLayer}
            onResetTransform={resetTransform}
          />
        )}
      </main>

      {isMobile && (
        <MobileBottomBar
          layerCount={layers.length}
          toolsPanel={
            <ToolPanel
              onUploadFiles={onUploadFiles}
              onAddLibraryImage={onAddLibraryImage}
              background={background}
              setBackground={setBackgroundWithHistory}
              onSmartArrange={onSmartArrange}
              onAiArrange={onAiArrange}
              layerCount={layers.length}
              arrangingMode={arrangingMode}
              lastStyleName={lastStyleName}
            />
          }
          layersPanel={
            <LayersPanel
              layers={layers}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onMoveUp={(id) => moveLayerZ(id, 'up')}
              onMoveDown={(id) => moveLayerZ(id, 'down')}
              onDelete={deleteLayer}
              onDuplicate={duplicateLayer}
              onResetTransform={resetTransform}
            />
          }
        />
      )}

      <ExportModal
        open={exportOpen}
        onClose={() => {
          if (busyMode === 'paid') return; // don't allow closing mid-payment
          setExportOpen(false);
          setPaymentStatus(null);
        }}
        onFreePreview={handleFreePreview}
        onBuyHd={handleBuyHd}
        busyMode={busyMode}
        paymentStatus={paymentStatus}
      />

      <GiftLinkModal
        open={giftModalOpen}
        onClose={() => setGiftModalOpen(false)}
        giftId={giftLink?.giftId}
        expiresAt={giftLink?.expiresAt}
      />
    </div>
  );
}

/* ---------- Stripe popup landing page ---------- */
function PaymentLanding() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const paid = params.get('paid') === '1';
    if (sessionId && paid && window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(
          { type: 'stickr-payment-complete', sessionId },
          window.location.origin,
        );
      } catch (_) { /* ignore */ }
    }
    const t = setTimeout(() => { try { window.close(); } catch (_) { /* ignore */ } }, 1200);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-canvas grain p-8">
      <div className="neo-card p-10 max-w-md text-center">
        <div className="h-16 w-16 mx-auto border-2 border-ink bg-mint shadow-brut flex items-center justify-center mb-4 rotate-[-4deg]">
          <span className="font-outfit font-black text-2xl">✓</span>
        </div>
        <h1 className="font-outfit font-black text-3xl">Payment received!</h1>
        <p className="font-work text-sm text-ink/70 mt-2">You can close this tab — your HD file is downloading in the original window.</p>
      </div>
    </div>
  );
}

export default function App() {
  const [view, setView] = useState('hero');

  // If this window is a Stripe redirect popup, show the landing page only.
  const params = new URLSearchParams(window.location.search);
  const isPopupReturn = !!params.get('session_id') && !!window.opener && !window.opener.closed;

  if (isPopupReturn) {
    return <PaymentLanding />;
  }

  // Path-based routing: /gift/{id} renders the public gift page
  const giftMatch = window.location.pathname.match(/^\/gift\/([A-Za-z0-9_-]{4,64})\/?$/);
  if (giftMatch) {
    return (
      <div className="App">
        <GiftView giftId={giftMatch[1]} />
        <Toaster
          position="bottom-center"
          toastOptions={{
            style: {
              border: '2px solid #0A0A0A',
              boxShadow: '4px 4px 0 0 #0A0A0A',
              background: '#FFFFFF',
              color: '#0A0A0A',
              borderRadius: '2px',
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 600,
            },
          }}
        />
      </div>
    );
  }

  return (
    <div className="App">
      {view === 'hero' ? (
        <Hero onStart={() => setView('editor')} />
      ) : (
        <Editor onBackHome={() => setView('hero')} />
      )}
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            border: '2px solid #0A0A0A',
            boxShadow: '4px 4px 0 0 #0A0A0A',
            background: '#FFFFFF',
            color: '#0A0A0A',
            borderRadius: '2px',
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 600,
          },
        }}
      />
    </div>
  );
}
